import * as Dfns from 'date-fns/fp';
import * as R from 'ramda';
import { DATE_FORMAT, snapToMonday } from '@src/utils/date-utils';
import { GoalRow, GoalSelectorOptions } from './goal-intake-constants';
import { channelsOf, intersects, validateGoal } from './goal-intake-utils';

export const GoalView = {
  MONTH: 'Month',
  WEEK: 'Week',
  CHANNEL: 'Channel'
} as const;
export type GoalViewType = (typeof GoalView)[keyof typeof GoalView];

// A row being edited. Saved rows are keyed by their id; new rows get a temporary `new-N` key.
export interface DraftRow {
  key: string;
  row: GoalRow;
}

export interface DraftGroup {
  key: string;
  label: string;
  drafts: DraftRow[];
}

export const NEW_ROW_KEY_PREFIX = 'new-';
const NO_DATE_LABEL = 'No date';

export const toDrafts = (rows: GoalRow[]): DraftRow[] =>
  rows.map(row => ({ key: String(row.id), row }));

const getGroup = (startDate: string | undefined, view: GoalViewType) => {
  if (!startDate) return { key: '', label: NO_DATE_LABEL };
  if (view === GoalView.MONTH) {
    return {
      key: startDate.slice(0, 7),
      label: Dfns.format('MMMM yyyy', Dfns.parseISO(startDate))
    };
  }
  const monday = snapToMonday(startDate);
  return {
    key: monday,
    label: `Week of ${Dfns.format('MMM d, yyyy', Dfns.parseISO(monday))}`
  };
};

// Regroups rows under month or "Week of" (Monday) headers, newest first, rows without a start
// date on top. View-only: it never changes the rows themselves.
export const groupDrafts = (drafts: DraftRow[], view: GoalViewType): DraftGroup[] => {
  const groups = new Map<string, DraftGroup>();
  R.sortWith([R.descend(d => d.row.start_date ?? '')], drafts).forEach(draft => {
    const { key, label } = getGroup(draft.row.start_date, view);
    const group = groups.get(key) ?? { key, label, drafts: [] };
    group.drafts.push(draft);
    groups.set(key, group);
  });
  // The no-date group has the empty key, which a plain descending sort would put last.
  return R.sortWith(
    [R.descend(group => (group.key === '' ? '\uffff' : group.key))],
    [...groups.values()]
  );
};

// A table row: a draft, or a Month/Week marker row (markerLabel set) that only labels what follows.
export interface TableItem extends DraftRow {
  markerLabel?: string;
  // Zebra stripe, counted over data rows only (BPMTable's own stripes would count markers).
  isStriped?: boolean;
}

// One flat list: a marker row, then that month's/week's rows. Stripes alternate across data rows
// only, so markers neither take a stripe nor shift the parity.
export const toTableItems = (groups: DraftGroup[]): TableItem[] => {
  let dataRowCount = 0;
  return groups.flatMap(group => [
    { key: `marker-${group.key}`, row: {}, markerLabel: group.label },
    ...group.drafts.map(draft => ({ ...draft, isStriped: dataRowCount++ % 2 === 1 }))
  ]);
};

export interface DraftDiff {
  insert: GoalRow[];
  update: GoalRow[];
  delete: GoalRow[];
  // Keys of new and edited rows, for the unsaved highlight.
  changedKeys: Set<string>;
}

export const diffDrafts = (drafts: DraftRow[], originals: GoalRow[]): DraftDiff => {
  const originalByKey = new Map(originals.map(row => [String(row.id), row]));
  const draftKeys = new Set(drafts.map(draft => draft.key));
  const insert: GoalRow[] = [];
  const update: GoalRow[] = [];
  const changedKeys = new Set<string>();
  drafts.forEach(({ key, row }) => {
    const original = originalByKey.get(key);
    if (!original) {
      insert.push(row);
      changedKeys.add(key);
    } else if (!R.equals(original, row)) {
      update.push(row);
      changedKeys.add(key);
    }
  });
  return {
    insert,
    update,
    delete: originals.filter(row => !draftKeys.has(String(row.id))),
    changedKeys
  };
};

// First validation error per new/edited row, checked against every other row on screen. Saved,
// untouched rows are not re-validated.
export const getRowErrors = (
  drafts: DraftRow[],
  changedKeys: Set<string>,
  customSegments: string[],
  selectorOptions: GoalSelectorOptions
): Map<string, string> => {
  const errors = new Map<string, string>();
  drafts
    .filter(({ key }) => changedKeys.has(key))
    .forEach(({ key, row }) => {
      const others = drafts.filter(draft => draft.key !== key).map(draft => draft.row);
      const { isValid, error } = validateGoal(row, others, customSegments, selectorOptions);
      if (!isValid && error) {
        errors.set(key, error);
      }
    });
  return errors;
};

// A legacy row's amount is the only thing that can be edited; an empty one would save as 0.
export const getLegacyErrors = (
  drafts: DraftRow[],
  changedKeys: Set<string>
): Map<string, string> =>
  new Map(
    drafts
      .filter(({ key, row }) => changedKeys.has(key) && row.goal_value == null)
      .map(({ key }) => [key, 'Goal Value cannot be empty'])
  );

// Mondays from the `newest` date's week back to the `oldest` date's week, newest first.
export const getChannelWeeks = (newest: string, oldest: string): string[] => {
  const weeks: string[] = [];
  for (let week = snapToMonday(newest); week >= snapToMonday(oldest);) {
    weeks.push(week);
    week = Dfns.format(DATE_FORMAT, Dfns.subDays(7, Dfns.parseISO(week)));
  }
  return weeks;
};

const weekEnd = (week: string): string =>
  Dfns.format(DATE_FORMAT, Dfns.addDays(6, Dfns.parseISO(week)));

// A budget that fills whole weeks: one merged cell over them. `startRow` is the newest of them.
export interface ChannelBlock {
  draft: DraftRow;
  startRow: number;
  span: number;
}

// A budget that only partly covers a week (or collides with another budget): a badge in that week.
export interface ChannelBadge {
  draft: DraftRow;
  row: number;
}

export interface ChannelColumn {
  blocks: ChannelBlock[];
  badges: ChannelBadge[];
  // Keys of budgets whose dates intersect another budget in this channel.
  overlapKeys: Set<string>;
}

// Lays one channel's budgets over `weeks` (newest first). Existing bookings are placed first, so
// they win a week over a Goal Intake budget; a budget that cannot get its weeks becomes badges.
export const layoutChannel = (drafts: DraftRow[], weeks: string[]): ChannelColumn => {
  const ordered = R.sortWith(
    [R.descend(d => (d.row.legacy ? 1 : 0)), R.ascend(d => d.row.start_date ?? '')],
    drafts.filter(d => d.row.start_date && d.row.end_date)
  );
  const occupied = new Set<number>();
  const blocks: ChannelBlock[] = [];
  const badges: ChannelBadge[] = [];
  const overlapKeys = new Set<string>();

  ordered.forEach((draft, index) => {
    const { start_date: start = '', end_date: end = '' } = draft.row;
    if (
      ordered.some((other, otherIndex) => otherIndex !== index && intersects(draft.row, other.row))
    ) {
      overlapKeys.add(draft.key);
    }
    const touched = weeks.flatMap((week, row) =>
      week <= end && weekEnd(week) >= start ? [row] : []
    );
    const full = touched.filter(row => start <= weeks[row] && end >= weekEnd(weeks[row]));
    if (full.length > 0 && !full.some(row => occupied.has(row))) {
      blocks.push({ draft, startRow: Math.min(...full), span: full.length });
      full.forEach(row => occupied.add(row));
      touched.filter(row => !full.includes(row)).forEach(row => badges.push({ draft, row }));
    } else {
      touched.forEach(row => badges.push({ draft, row }));
      if (full.length > 0) {
        overlapKeys.add(draft.key);
      }
    }
  });
  return { blocks, badges, overlapKeys };
};

// The channels that have a Budget row, sorted; rows without a Channel or dates are not placed.
export const getChannelDrafts = (drafts: DraftRow[]): Map<string, DraftRow[]> => {
  const byChannel = new Map<string, DraftRow[]>();
  drafts.forEach(draft => {
    const { row } = draft;
    if (row.goal_name !== 'Budget' || !row.start_date || !row.end_date) {
      return;
    }
    channelsOf(row).forEach(channel => {
      byChannel.set(channel, [...(byChannel.get(channel) ?? []), draft]);
    });
  });
  return new Map([...byChannel.entries()].sort(([a], [b]) => a.localeCompare(b)));
};

// The weeks first shown in the Channel view: a year ending at the company's latest budget (at most
// `weeksAhead` past today), so a company whose budgets all ended months ago is not shown empty.
export const getInitialChannelWindow = (
  channelDrafts: Map<string, DraftRow[]>,
  today: string,
  weeksAhead: number,
  weeksShown: number
): { newest: string; oldest: string } => {
  const limit = Dfns.format(DATE_FORMAT, Dfns.addWeeks(weeksAhead, Dfns.parseISO(today)));
  const latest = [...channelDrafts.values()]
    .flat()
    .reduce((max, { row }) => ((row.end_date ?? '') > max ? (row.end_date ?? '') : max), '');
  const newest = latest && latest < limit ? latest : limit;
  return {
    newest,
    oldest: Dfns.format(DATE_FORMAT, Dfns.subWeeks(weeksShown, Dfns.parseISO(newest)))
  };
};

// Every week the Channel view scrolls over: from the earliest budget (or a year back with no data)
// up to the later of the opening week and `weeksAhead` past today. `open` is the week to scroll to.
export const getChannelRange = (
  channelDrafts: Map<string, DraftRow[]>,
  today: string,
  weeksAhead: number,
  weeksShown: number
): { newest: string; oldest: string; open: string } => {
  const { newest: open, oldest: yearBack } = getInitialChannelWindow(
    channelDrafts,
    today,
    weeksAhead,
    weeksShown
  );
  const earliest = [...channelDrafts.values()]
    .flat()
    .reduce(
      (min, { row }) => (row.start_date && row.start_date < min ? row.start_date : min),
      yearBack
    );
  const ahead = Dfns.format(DATE_FORMAT, Dfns.addWeeks(weeksAhead, Dfns.parseISO(today)));
  return { newest: open > ahead ? open : ahead, oldest: earliest, open };
};
